import React, {useMemo} from 'react';
import isEqual from 'react-fast-compare';
import {FormikT} from 'utils/types';
import {ChartQuery, ChartSettings} from 'entities/chart';
import CellHandlerContainer from '@/views/storyboard/cellhandlers/CellHandlerContainer';
import TrendValueString from '@/views/storyboard/charts/utils/TrendValueString';
import {ChartFooterData} from '@/views/storyboard/charts/utils/chartFooterData';
import {
    ChartSettingsTitleEditor,
    ChartSettingsTitleEditorRef,
} from '@/views/storyboard/chart_settings/ChartSettingsTitleEditor';
import i18n from 'i18n';

interface HeaderProps {
    chartSettingsFormik: FormikT<ChartSettings>;
    showTitleRequiredError?: boolean;
    titleEditorRef?: React.RefObject<ChartSettingsTitleEditorRef>;
}

interface ChartProps {
    chartSettingsFormik: FormikT<ChartSettings>;
    chartQueryFormik: FormikT<ChartQuery>;
    onFooterDataChange: (footerData: ChartFooterData) => void;
}

interface FooterProps {
    datePeriod: string;
    footerData: ChartFooterData;
}

const formatTotal = (total: ChartFooterData['total']) => {
    if (total === undefined || total === null || total === '') {
        return '';
    }

    if (typeof total === 'number') {
        return total.toLocaleString();
    }

    const numericTotal = Number(total);
    return Number.isFinite(numericTotal) ? numericTotal.toLocaleString() : total;
};

const formatQueryDuration = (queryDuration?: string) => {
    if (!queryDuration) {
        return '';
    }

    const numericDuration = Number(queryDuration);
    return Number.isFinite(numericDuration) ? `${numericDuration}s` : `${queryDuration}s`;
};

const chartSettingsWithoutTextFields = (
    chartSettings: ChartSettings,
): Omit<ChartSettings, 'title' | 'description'> => {
    const {title: _title, description: _description, ...chartSettingsWithoutText} = chartSettings;
    return chartSettingsWithoutText;
};

export const ChartSettingsPreviewHeader = React.memo<HeaderProps>(({
    chartSettingsFormik,
    showTitleRequiredError = false,
    titleEditorRef,
}) => {
    return (
        <div className="chart-settings-preview-card__head">
            <ChartSettingsTitleEditor
                ref={titleEditorRef}
                chartSettingsFormik={chartSettingsFormik}
                showTitleRequiredError={showTitleRequiredError}
            />
        </div>
    );
});

export const ChartSettingsPreviewChart = React.memo<ChartProps>(({
    chartSettingsFormik,
    chartQueryFormik,
    onFooterDataChange,
}) => {
    const chartSettingsDataSignature = JSON.stringify(
        chartSettingsWithoutTextFields(chartSettingsFormik.values),
    );
    const chartSettingsForPreview = useMemo(
        () => chartSettingsFormik.values,
        [chartSettingsDataSignature],
    );

    return (
        <div className="chart-settings-preview">
            <div className="chart-settings-preview__canvas">
                <div
                    className="chart-settings-form-chart-preview"
                    data-cy="chart-settings-form-chart-preview"
                >
                    <CellHandlerContainer
                        chart={chartSettingsForPreview}
                        chartQuery={chartQueryFormik.values}
                        previewOnly
                        chartSettingsView={true}
                        onPreviewFooterChange={onFooterDataChange}
                    />
                </div>
            </div>
        </div>
    );
}, (prevProps, nextProps) => (
    prevProps.chartQueryFormik.values === nextProps.chartQueryFormik.values
    && prevProps.onFooterDataChange === nextProps.onFooterDataChange
    && isEqual(
        chartSettingsWithoutTextFields(prevProps.chartSettingsFormik.values),
        chartSettingsWithoutTextFields(nextProps.chartSettingsFormik.values),
    )
));

export const ChartSettingsPreviewFooter = React.memo<FooterProps>(({datePeriod, footerData}) => {
    const total = footerData.hideTotal ? '' : formatTotal(footerData.total);
    const queryDuration = formatQueryDuration(footerData.queryDuration);
    const showTrend = footerData.showTrend && footerData.trendValue && Number(footerData.trendValue) !== 0;

    return (
        <div className="chart-settings-preview-footer">
            <div className="chart-settings-preview-footer__date">
                {datePeriod}
            </div>
            <div className="chart-settings-preview-footer__total">
                {total ? (
                    <>
                        <span>{i18n.t('chart_settings_total.text')}</span>
                        <strong>{total}</strong>
                        {showTrend ? (
                            <TrendValueString
                                value={footerData.trendValue!}
                                comparableValue={footerData.totalComparable}
                            />
                        ) : null}
                    </>
                ) : null}
            </div>
            <div className="chart-settings-preview-footer__query">
                {queryDuration ? (
                    <>
                        <span>{i18n.t('chart_settings_query_with_colon.text')}</span>
                        <span>{queryDuration}</span>
                    </>
                ) : null}
            </div>
        </div>
    );
});
